/**
 * V126 Carril L (#310) — GastroGest: Paquetes prix-fixe (menú por tiempos).
 * Un paquete = producto a PRECIO FIJO donde el cliente elige 1 opción por tiempo
 * (entrada + fuerte + postre…). Reusa V86: alta/edición llaman a los endpoints
 * transaccionales POST/PUT /gastro/packages que crean/reconcilian producto
 * (is_prix_fixe) + grupos elige-1 + opciones en UNA transacción (atómico). El
 * costeo = suma de los costos de las opciones elegidas (margen); costo base opcional.
 *
 * Editar un paquete se hace COMO paquete (mismo wizard pre-llenado, no como grupos
 * de modificadores sueltos). Gateado a gastro + product_catalog:write.
 */
import { useEffect, useState } from 'react';
import { Package, Pencil, Plus, Trash2, X, Layers, Carrot } from 'lucide-react';
import { api } from '../lib/api';
import { OptionRecipeModal } from './ModifiersView';

const mxn = (v: any) =>
  `$${Number(v ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const btnPrimary = 'rounded-xl bg-[var(--brand-primary)] px-4 py-2.5 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--brand-primary)]';
const btnGhost = 'rounded-xl bg-white px-4 py-2.5 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white';

interface PkgOption { id: string; name: string; priceDelta: number; cost: number | null; }
interface PkgCourse { id: string; name: string; minSelect: number; maxSelect: number | null; options: PkgOption[]; }
interface Pkg {
  id: string; name: string; basePrice: number; cost: number | null;
  category: string; isActive: boolean; isPrixFixe: boolean;
  modifierGroups?: PkgCourse[];
}

export function PaquetesView() {
  const [packages, setPackages] = useState<Pkg[]>([]);
  const [loading, setLoading] = useState(true);
  // 'new' = alta; un Pkg = edición de ese paquete; null = cerrado.
  const [wizard, setWizard] = useState<Pkg | 'new' | null>(null);

  const load = async () => {
    setLoading(true);
    try { setPackages((await api('/gastro/packages?includeInactive=true')) as Pkg[]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="mb-5 flex items-end justify-between">
        <div>
          <h2 className="font-serif text-2xl tracking-tight text-slate-900">Paquetes</h2>
          <p className="text-sm text-slate-500">Menú por tiempos a precio fijo: el cliente elige 1 opción por tiempo (entrada + fuerte + postre).</p>
        </div>
        <button className={btnPrimary} onClick={() => setWizard('new')}>+ Paquete</button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Cargando…</p>
      ) : packages.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 p-10 text-center text-sm text-slate-400">
          Crea tu primer paquete con “+ Paquete” (ej. Menú Ejecutivo $180: Entrada / Plato fuerte / Postre).
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {packages.map((p) => (
            <div key={p.id} className={`rounded-2xl border p-4 ${p.isActive ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-60'}`}>
              <div className="mb-2 flex items-center justify-between">
                <span className="flex items-center gap-2 font-serif text-lg text-slate-900">
                  <Package className="h-4 w-4 text-slate-400" /> {p.name}
                  {!p.isActive && <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-600">Inactivo</span>}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-base font-semibold tabular-nums text-slate-900">{mxn(p.basePrice)}</span>
                  <button onClick={() => setWizard(p)} title="Editar paquete"
                    className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 ring-1 ring-slate-200 hover:bg-slate-50">
                    <Pencil className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-slate-400">
                {(p.modifierGroups ?? []).length} tiempo{(p.modifierGroups ?? []).length === 1 ? '' : 's'}
                {p.cost != null && Number(p.cost) > 0 ? ` · costo base ${mxn(p.cost)}` : ''}
              </p>
              <div className="space-y-1.5">
                {(p.modifierGroups ?? []).map((g) => (
                  <div key={g.id} className="rounded-xl bg-slate-50 px-3 py-2">
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
                      <Layers className="h-3.5 w-3.5 text-slate-400" /> {courseLabel(g.name, p.name)}
                    </span>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {g.options.map((o) => (
                        <span key={o.id} className="rounded-full bg-white px-2 py-0.5 text-[11px] text-slate-600 ring-1 ring-slate-200">
                          {o.name}{o.cost != null ? <span className="text-slate-400"> · {mxn(o.cost)}</span> : null}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {wizard && (
        <PackageWizard
          editing={wizard === 'new' ? null : wizard}
          onClose={() => setWizard(null)}
          onSaved={() => { setWizard(null); load(); }}
        />
      )}
    </div>
  );
}

/** Los grupos se nombran "Paquete: Tiempo"; en la UI mostramos solo el tiempo. */
function courseLabel(groupName: string, pkgName: string): string {
  const prefix = `${pkgName}: `;
  return groupName.startsWith(prefix) ? groupName.slice(prefix.length) : groupName;
}

// ===========================================================================
// Wizard de alta/edición — nombre + precio fijo + tiempos (con sus opciones)
// ===========================================================================
interface DraftOption { id?: string; name: string; cost: string; }
interface DraftCourse { id?: string; name: string; options: DraftOption[]; }

function PackageWizard({ editing, onClose, onSaved }: {
  editing: Pkg | null; onClose: () => void; onSaved: () => void;
}) {
  const isEdit = !!editing;
  const [name, setName] = useState(editing?.name ?? '');
  const [price, setPrice] = useState(editing ? String(Number(editing.basePrice)) : '');
  const [baseCost, setBaseCost] = useState(editing?.cost != null ? String(Number(editing.cost)) : '');
  const [courses, setCourses] = useState<DraftCourse[]>(
    editing
      ? (editing.modifierGroups ?? []).map((g) => ({
          id: g.id,
          name: courseLabel(g.name, editing.name),
          options: g.options.map((o) => ({ id: o.id, name: o.name, cost: o.cost != null ? String(Number(o.cost)) : '' })),
        }))
      : [{ name: '', options: [{ name: '', cost: '' }] }],
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // V127 — receta de insumos de una opción ya persistida (con id).
  const [recipeOpt, setRecipeOpt] = useState<{ id: string; name: string } | null>(null);

  const setCourse = (i: number, patch: Partial<DraftCourse>) =>
    setCourses((xs) => xs.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const addCourse = () => setCourses((xs) => [...xs, { name: '', options: [{ name: '', cost: '' }] }]);
  const removeCourse = (i: number) => setCourses((xs) => xs.filter((_, j) => j !== i));

  const setOption = (ci: number, oi: number, patch: Partial<DraftOption>) =>
    setCourses((xs) => xs.map((c, j) => (j === ci
      ? { ...c, options: c.options.map((o, k) => (k === oi ? { ...o, ...patch } : o)) }
      : c)));
  const addOption = (ci: number) =>
    setCourses((xs) => xs.map((c, j) => (j === ci ? { ...c, options: [...c.options, { name: '', cost: '' }] } : c)));
  const removeOption = (ci: number, oi: number) =>
    setCourses((xs) => xs.map((c, j) => (j === ci ? { ...c, options: c.options.filter((_, k) => k !== oi) } : c)));

  // Validación de UI (el server re-valida y es la fuente de verdad). Conserva ids
  // (para reconciliar en edición) y descarta opciones sin nombre.
  const cleanCourses = courses.map((c) => ({
    id: c.id,
    name: c.name.trim(),
    options: c.options.map((o) => ({ id: o.id, name: o.name.trim(), cost: o.cost.trim() })).filter((o) => o.name),
  }));
  const valid =
    name.trim().length > 0 &&
    price !== '' && Number(price) >= 0 &&
    cleanCourses.length > 0 &&
    cleanCourses.every((c) => c.name && c.options.length > 0);

  const save = async () => {
    if (!valid) return;
    setBusy(true); setErr(null);
    try {
      const body = {
        name: name.trim(),
        price: Number(price) || 0,
        ...(baseCost.trim() !== '' ? { cost: Number(baseCost) || 0 } : (isEdit ? { cost: 0 } : {})),
        courses: cleanCourses.map((c) => ({
          ...(c.id ? { id: c.id } : {}),
          name: c.name,
          options: c.options.map((o) => ({
            ...(o.id ? { id: o.id } : {}),
            name: o.name,
            ...(o.cost !== '' ? { cost: Number(o.cost) || 0 } : {}),
          })),
        })),
      };
      if (isEdit) await api(`/gastro/packages/${editing!.id}`, { method: 'PUT', body });
      else await api('/gastro/packages', { method: 'POST', body });
      onSaved();
    } catch (e: any) {
      setErr(e?.body?.message || e?.message || 'No se pudo guardar el paquete');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <h3 className="font-serif text-lg text-slate-900">{isEdit ? `Editar “${editing!.name}”` : 'Nuevo paquete'}</h3>
          <button onClick={onClose} className="grid h-10 w-10 place-items-center text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <label className="mb-1 block text-xs font-medium text-slate-500">Nombre del paquete</label>
          <input className="input mb-4 min-h-12 text-base" placeholder="Ej. Menú Ejecutivo"
            value={name} onChange={(e) => setName(e.target.value)} />

          <div className="mb-4 grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Precio fijo</label>
              <input className="input min-h-12 text-base" type="number" min="0" step="0.01" placeholder="180.00"
                value={price} onChange={(e) => setPrice(e.target.value)} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Costo base (opcional)</label>
              <input className="input min-h-12 text-base" type="number" min="0" step="0.01" placeholder="0.00"
                value={baseCost} onChange={(e) => setBaseCost(e.target.value)} />
            </div>
          </div>
          <p className="mb-4 -mt-2 text-[10px] text-slate-400">
            Costo base = pan/cubierto/salsas del paquete (opcional). El resto del costo sale de cada tiempo.
          </p>

          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Tiempos</p>
          <div className="space-y-3">
            {courses.map((c, ci) => (
              <div key={c.id ?? `new-${ci}`} className="rounded-2xl border border-slate-200 p-3">
                <div className="mb-2 flex items-center gap-2">
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-slate-900 text-xs font-semibold text-white">{ci + 1}</span>
                  <input className="input min-h-11 flex-1" placeholder="Nombre del tiempo (ej. Entrada)"
                    value={c.name} onChange={(e) => setCourse(ci, { name: e.target.value })} />
                  <button onClick={() => removeCourse(ci)} disabled={courses.length === 1} title="Quitar tiempo"
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-lg text-slate-400 ring-1 ring-slate-200 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-30">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <p className="mb-1.5 ml-9 text-[10px] uppercase tracking-wide text-slate-400">Opciones (elige 1 al vender) · nombre · costo opcional</p>
                <div className="ml-9 space-y-1.5">
                  {c.options.map((o, oi) => (
                    <div key={o.id ?? `new-${oi}`} className="grid grid-cols-[minmax(0,1fr)_5rem_2.5rem_2.5rem] items-center gap-2">
                      <input className="input min-h-11" placeholder="Nombre de la opción (ej. Sopa)"
                        value={o.name} onChange={(e) => setOption(ci, oi, { name: e.target.value })} />
                      <input className="input min-h-11" type="number" min="0" step="0.01" placeholder="costo"
                        value={o.cost} onChange={(e) => setOption(ci, oi, { cost: e.target.value })} />
                      {/* V127 — receta de insumos: solo para opciones ya guardadas (con id). */}
                      <button onClick={() => o.id && setRecipeOpt({ id: o.id, name: o.name || 'opción' })}
                        disabled={!o.id} title={o.id ? 'Receta de insumos (food-cost + descuento de stock)' : 'Guarda el paquete primero para ligar insumos'}
                        className="grid h-10 w-10 place-items-center rounded-lg text-slate-400 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-30">
                        <Carrot className="h-4 w-4" />
                      </button>
                      <button onClick={() => removeOption(ci, oi)} disabled={c.options.length === 1} title="Quitar opción"
                        className="grid h-10 w-10 place-items-center rounded-lg text-slate-400 ring-1 ring-slate-200 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-30">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <button onClick={() => addOption(ci)} className="text-xs font-medium text-[var(--brand-primary)] hover:underline">
                    + Agregar opción
                  </button>
                </div>
              </div>
            ))}
          </div>
          <button onClick={addCourse} className="mt-3 flex min-h-11 w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-slate-300 text-sm font-medium text-slate-600 hover:bg-slate-50">
            <Plus className="h-4 w-4" /> Agregar tiempo
          </button>
        </div>

        <div className="border-t border-slate-100 px-6 py-4">
          {err && <p className="alert-over mb-3 rounded-xl px-3 py-2 text-xs">{err}</p>}
          <div className="flex justify-end gap-2">
            <button className={btnGhost} onClick={onClose} disabled={busy}>Cancelar</button>
            <button className={btnPrimary} onClick={save} disabled={busy || !valid}>
              {busy ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Crear paquete'}
            </button>
          </div>
        </div>
        {recipeOpt && <OptionRecipeModal option={recipeOpt} onClose={() => setRecipeOpt(null)} />}
      </div>
    </div>
  );
}
